import { parseDate, formatISODate } from '../../../../common/utils/common.util';
import { SectionFieldCollection, FieldType } from '../magic-report.type';
import { getWeeksBehindDirection, getWeeksBehindMagnitudeDisplay } from './field-renderers/WeeksBehindFieldRenderer';

export const PAYAPP_STATIC_SECTION_IDS = [
  'previous-fundings',
  'change-orders',
  'potential-change-orders',
  'reallocations',
  'retainage',
  'stored-materials',
  'lien-waivers',
  'notary-and-signatures',
  'minor-adjustments',
] as const;

export const DOCUMENT_STATIC_SECTION_IDS = [
  'progress-description',
  'schedule',
  'material-testing-reports',
  'construction-meeting-minutes',
  'docs-received',
  'foundation-survey',
  'plan-cost-review-issues',
  'project-description',
] as const;

export type MagicReportStaticSectionId = (typeof PAYAPP_STATIC_SECTION_IDS)[number] | (typeof DOCUMENT_STATIC_SECTION_IDS)[number];

export const ALL_ORDERED_STATIC_SECTION_IDS: MagicReportStaticSectionId[] = [...PAYAPP_STATIC_SECTION_IDS, ...DOCUMENT_STATIC_SECTION_IDS];

/** @alias ALL_ORDERED_STATIC_SECTION_IDS */
export const STATIC_SECTION_IDS = ALL_ORDERED_STATIC_SECTION_IDS;

export const STATIC_SECTION_DISPLAY_TITLES: Record<MagicReportStaticSectionId, string> = {
  'previous-fundings': 'Previous Fundings',
  'change-orders': 'Change Orders',
  'potential-change-orders': 'Potential Change Orders',
  reallocations: 'Reallocations',
  retainage: 'Retainage',
  'stored-materials': 'Stored Materials',
  'lien-waivers': 'Lien Releases / Waivers',
  'notary-and-signatures': 'Signatures and Notary',
  'minor-adjustments': 'Minor Adjustments',
  'progress-description': 'Desc. of Construction Progress',
  schedule: 'Construction Schedule',
  'material-testing-reports': 'Materials Testing',
  'construction-meeting-minutes': 'Construction Meeting Minutes',
  'docs-received': 'Documents Received',
  'foundation-survey': 'Foundation Survey',
  'plan-cost-review-issues': 'Initial Plan/Cost Review Issues',
  'project-description': 'Project Description',
};

const buildSubsteps = <T extends readonly MagicReportStaticSectionId[]>(ids: T) =>
  ids.map(id => ({
    id,
    title: STATIC_SECTION_DISPLAY_TITLES[id],
  }));

/** Special substep rendered at the top of Payapp Review — not tied to a `reportState.sections` key. */
export const COVER_LETTER_AMOUNTS_SUBSTEP = { id: 'review-budget-cover-letter-amounts', title: 'Amounts for Cover Letter' } as const;

export const PAYAPP_SUBSTEPS = buildSubsteps(PAYAPP_STATIC_SECTION_IDS);
export const DOCUMENT_SUBSTEPS = buildSubsteps(DOCUMENT_STATIC_SECTION_IDS);

/** Maps wizard substep IDs (kebab-case) to reportState.sections keys (camelCase config IDs) */
export const SUBSTEP_ID_TO_SECTION_KEY: Record<MagicReportStaticSectionId, string> = {
  'previous-fundings': 'previous-fundings',
  'change-orders': 'changeOrders',
  'potential-change-orders': 'potentialChangeOrders',
  reallocations: 'reallocations',
  retainage: 'retainage',
  'stored-materials': 'storedMaterials',
  'lien-waivers': 'lienWaivers',
  'notary-and-signatures': 'notaryAndSignatures',
  'minor-adjustments': 'minorAdjustments',
  'progress-description': 'progressDescription',
  schedule: 'schedule',
  'material-testing-reports': 'materialTestingReports',
  'construction-meeting-minutes': 'constructionMeetingMinutes',
  'docs-received': 'docsReceived',
  'foundation-survey': 'foundationSurvey',
  'plan-cost-review-issues': 'planCostReviewIssues',
  'project-description': 'projectDescription',
};

export const REVIEW_BUDGET_SUBSTEPS = [
  { id: 'review-budget-g702-3', title: 'G702/3' },
  { id: 'review-budget-application-comments', title: 'Application Comments' },
  { id: 'review-budget-reduction-comments', title: 'Reduction Comments' },
] as const;

/**
 * Review Budget substeps for a multi-draw report: each draw contributes a non-clickable header
 * carrying its name, followed by that draw's own three substeps. Substep ids are suffixed with
 * the draw id (see drawScopedKey) so each draw's anchors and completion state stay independent.
 */
export const buildReviewBudgetSubSteps = (
  draws: Array<{ drawId: string; drawSummary?: { name?: string } }>,
): Array<{ id: string; title: string; isGroupHeader?: boolean }> => {
  if (!draws || draws.length === 0) {
    return REVIEW_BUDGET_SUBSTEPS.map(step => ({ ...step }));
  }

  // A single draw needs no grouping — keep the original flat, unsuffixed substeps so existing
  // reports look and behave exactly as before.
  if (draws.length === 1) {
    return REVIEW_BUDGET_SUBSTEPS.map(step => ({ ...step, id: `${step.id}__${draws[0].drawId}` }));
  }

  return draws.flatMap((draw, index) => [
    {
      id: `review-budget-draw-header__${draw.drawId}`,
      title: draw.drawSummary?.name || `Draw ${index + 1}`,
      isGroupHeader: true,
    },
    ...REVIEW_BUDGET_SUBSTEPS.map(step => ({ ...step, id: `${step.id}__${draw.drawId}` })),
  ]);
};

export const STATIC_SECTION_TITLES = ALL_ORDERED_STATIC_SECTION_IDS.map(id => STATIC_SECTION_DISPLAY_TITLES[id]);

export const RESERVED_SECTION_TITLES = [
  ...STATIC_SECTION_TITLES,
  ...REVIEW_BUDGET_SUBSTEPS.map(step => step.title),
  COVER_LETTER_AMOUNTS_SUBSTEP.title,
];

/**
 * Normalizes field types from string to number for backward compatibility
 * This handles cases where the backend might return string enum names instead of numeric values
 */
export const normalizeFieldTypes = (data: SectionFieldCollection): SectionFieldCollection => {
  const typeMap: Record<string, FieldType> = {
    Boolean: FieldType.Boolean,
    Select: FieldType.Select,
    Multiselect: FieldType.Multiselect,
    Text: FieldType.Text,
    TextArray: FieldType.TextArray,
    Date: FieldType.Date,
    Number: FieldType.Number,
  };

  return {
    ...data,
    fields: data.fields.map(field => ({
      ...field,
      fieldType: typeof field.fieldType === 'string' ? (typeMap[field.fieldType] ?? field.fieldType) : field.fieldType,
    })),
  };
};

/**
 * Helper function to find a field by name in a section's field collection
 */
export const getField = (data: SectionFieldCollection | null, fieldName: string) => {
  return data?.fields.find(f => f.fieldName === fieldName);
};

/**
 * Helper function to get the comments field from a section
 */
export const getCommentsField = (
  data: SectionFieldCollection,
  config?: { isCommentField?: (fieldName: string, fieldType: FieldType) => boolean },
) => {
  return data.fields.find(f => {
    if (config?.isCommentField) {
      return config.isCommentField(f.fieldName, f.fieldType);
    }
    // Default: TextArray field with "comment" in the name
    return f.fieldType === FieldType.TextArray && f.fieldName.toLowerCase().includes('comment');
  });
};

/**
 * Calc Weeks Ahead (Behind) based on GC Numbers.
 *
 * 1. Weeks passed = Period End − Commencement Date
 * 2. Average weekly progress = Completed + Stored to Date ÷ Weeks passed
 * 3. Estimated weeks to complete = Balance to Complete ÷ Average weekly progress
 * 4. Remaining calendar weeks = Revised Completion Date − Period End
 * 5. Result = Remaining calendar weeks − Estimated weeks to complete
 *
 * Sign convention: negative = behind schedule, positive = ahead of schedule (matches
 * WeeksBehindToReport). Rounded to the nearest whole week — no decimals.
 */
export function calculateWeeksAheadBehind(projectSummary: any, budgetSummary: any, drawSummary: any): string {
  const commenceDate = parseDate(projectSummary?.commencementDate);
  if (!commenceDate) {
    return 'Cannot calculate (missing commencement date)';
  }

  const periodEndingDate = parseDate(drawSummary?.periodEnding);
  if (!periodEndingDate) {
    return 'Cannot calculate (missing period ending date)';
  }

  const completionDate = parseDate(projectSummary?.revisedCompletionDate) || parseDate(projectSummary?.originalCompletionDateEstimated);
  if (!completionDate) {
    return 'Cannot calculate (assign completion date)';
  }

  const toWeeks = (end: Date, start: Date) => (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24 * 7);

  const weeksPassed = toWeeks(periodEndingDate, commenceDate);
  if (!Number.isFinite(weeksPassed) || weeksPassed <= 0) {
    return 'Cannot calculate (invalid elapsed weeks)';
  }

  const totalCompleted = budgetSummary?.totalCompletedAndStored;
  if (totalCompleted === undefined || totalCompleted === null) {
    return 'Cannot calculate (missing completed & stored)';
  }

  const balanceToComplete = (budgetSummary as any)?.balanceToComplete ?? budgetSummary?.balanceToCompletePlusRetainage ?? null;
  if (balanceToComplete === null || balanceToComplete === undefined) {
    return 'Cannot calculate (missing balance to complete)';
  }

  const avgWeeklyProgress = totalCompleted / weeksPassed;
  if (!Number.isFinite(avgWeeklyProgress) || avgWeeklyProgress === 0) {
    return 'Cannot calculate (invalid weekly progress rate)';
  }

  const estimatedWeeksToComplete = balanceToComplete / avgWeeklyProgress;
  if (!Number.isFinite(estimatedWeeksToComplete)) {
    return 'Cannot calculate (invalid estimated weeks to complete)';
  }

  const remainingWeeks = toWeeks(completionDate, periodEndingDate);
  if (!Number.isFinite(remainingWeeks)) {
    return 'Cannot calculate (invalid remaining weeks)';
  }

  const weeksAheadBehind = remainingWeeks - estimatedWeeksToComplete;
  if (!Number.isFinite(weeksAheadBehind)) {
    return 'Cannot calculate (invalid weeks value)';
  }

  return Math.round(weeksAheadBehind).toString();
}

/**
 * Schedule calculation modes (persisted in the ScheduleCalculationMode field).
 * Blank/unknown is treated as SINGLE so reports created before this field existed keep their
 * previous behaviour (one G702 drives the calculation).
 */
export const SCHEDULE_CALC_MODES = {
  single: 'Single',
  all: 'All',
  none: 'NoCalculation',
} as const;
export type ScheduleCalcMode = (typeof SCHEDULE_CALC_MODES)[keyof typeof SCHEDULE_CALC_MODES];

/** Shown in Calc Weeks Ahead (Behind) when the inspector opts out of the calculation entirely. */
export const SCHEDULE_NO_CALCULATION_TEXT = 'No Calculation';

export const parseScheduleCalcMode = (value?: string): ScheduleCalcMode => {
  const v = (value || '').trim();
  if (v === SCHEDULE_CALC_MODES.all) return SCHEDULE_CALC_MODES.all;
  if (v === SCHEDULE_CALC_MODES.none) return SCHEDULE_CALC_MODES.none;
  return SCHEDULE_CALC_MODES.single;
};

/** Human label for a covered G702, matching how every other multi-draw section names them. */
export const getScheduleBudgetLabel = (draw: { drawSummary?: { name?: string } } | undefined, index: number): string =>
  draw?.drawSummary?.name?.trim() || `Draw ${index + 1}`;

/**
 * Resolve the G702 a Single-mode calculation runs against.
 *
 * The stored value is a drawId, but reports written before this change stored the draw *name*
 * (that was all the old read-only select could hold), so fall back to a name match and finally to
 * the primary draw — which is what the calculation silently used before budgets were selectable.
 */
export function resolveScheduleBudgetDraw<T extends { drawId?: string; drawSummary?: { id?: string; name?: string } }>(
  draws: T[] | undefined,
  storedValue?: string,
): T | undefined {
  if (!draws?.length) return undefined;
  const stored = (storedValue || '').trim();
  if (stored) {
    const byId = draws.find(d => d.drawId === stored || d.drawSummary?.id === stored);
    if (byId) return byId;
    const byName = draws.find(d => (d.drawSummary?.name || '').trim() === stored);
    if (byName) return byName;
  }
  return draws[draws.length - 1];
}

/**
 * Calc Weeks Ahead (Behind) across every covered G702/3 ("All" mode).
 *
 * All G702s share the project's single Commencement Date, and the elapsed time for the combined
 * progress rate is measured to the LAST period ending of any covered G702:
 *
 * 1. Weeks passed = last Period End of all G702s − Commencement Date
 * 2. Combined weekly progress = sum(Completed + Stored to Date) ÷ Weeks passed
 * 3. Balance to complete = sum(Contract Budget Sum to Date) − sum(Completed + Stored to Date)
 *    (retainage-exclusive, per the worked example)
 * 4. Funding weeks remaining = Balance to complete ÷ Combined weekly progress
 * 5. Calendar weeks remaining = (Revised, else Original completion date) − last Period End
 * 6. Result = Calendar weeks remaining − Funding weeks remaining
 *
 * Same sign convention and whole-week rounding as the single-G702 calculation above.
 */
export function calculateWeeksAheadBehindAllBudgets(
  projectSummary: any,
  draws: { drawSummary?: any; budgetSummary?: any }[] | undefined,
): string {
  const commenceDate = parseDate(projectSummary?.commencementDate);
  if (!commenceDate) {
    return 'Cannot calculate (missing commencement date)';
  }

  const withBudgets = (draws ?? []).filter(d => d.budgetSummary);
  if (withBudgets.length === 0) {
    return 'Cannot calculate (no budgets)';
  }

  const periodEndDates = withBudgets.map(d => parseDate(d.drawSummary?.periodEnding)).filter((d): d is Date => Boolean(d));
  if (periodEndDates.length !== withBudgets.length) {
    return 'Cannot calculate (missing period ending date)';
  }
  const lastPeriodEnd = new Date(Math.max(...periodEndDates.map(d => d.getTime())));

  const completionDate = parseDate(projectSummary?.revisedCompletionDate) || parseDate(projectSummary?.originalCompletionDateEstimated);
  if (!completionDate) {
    return 'Cannot calculate (assign completion date)';
  }

  const toWeeks = (end: Date, start: Date) => (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24 * 7);

  const weeksPassed = toWeeks(lastPeriodEnd, commenceDate);
  if (!Number.isFinite(weeksPassed) || weeksPassed <= 0) {
    return 'Cannot calculate (invalid elapsed weeks)';
  }

  let totalCompleted = 0;
  let totalContract = 0;
  for (const draw of withBudgets) {
    const completed = draw.budgetSummary?.totalCompletedAndStored;
    const contract = draw.budgetSummary?.contractBudgetSumToDate;
    if (completed === undefined || completed === null) {
      return 'Cannot calculate (missing completed & stored)';
    }
    if (contract === undefined || contract === null) {
      return 'Cannot calculate (missing contract budget)';
    }
    totalCompleted += completed;
    totalContract += contract;
  }

  const avgWeeklyProgress = totalCompleted / weeksPassed;
  if (!Number.isFinite(avgWeeklyProgress) || avgWeeklyProgress === 0) {
    return 'Cannot calculate (invalid weekly progress rate)';
  }

  const balanceToComplete = totalContract - totalCompleted;
  const fundingWeeksRemaining = balanceToComplete / avgWeeklyProgress;
  if (!Number.isFinite(fundingWeeksRemaining)) {
    return 'Cannot calculate (invalid estimated weeks to complete)';
  }

  const calendarWeeksRemaining = toWeeks(completionDate, lastPeriodEnd);
  if (!Number.isFinite(calendarWeeksRemaining)) {
    return 'Cannot calculate (invalid remaining weeks)';
  }

  const weeksAheadBehind = calendarWeeksRemaining - fundingWeeksRemaining;
  if (!Number.isFinite(weeksAheadBehind)) {
    return 'Cannot calculate (invalid weeks value)';
  }

  return Math.round(weeksAheadBehind).toString();
}

/**
 * Schedule section: get the "latest" completion date that is populated (Revised > CDCC Estimated > Original)
 * and return its display name and formatted date (e.g. "January 1, 2026").
 */
export function getLatestCompletionDateForSchedule(data: SectionFieldCollection): { name: string; dateFormatted: string } | null {
  const getVal = (name: string) => data?.fields.find(f => f.fieldName === name)?.value?.trim() ?? '';
  const candidates = [
    { fieldName: 'RevisedCompletionDate', displayName: 'Revised Completion Date' },
    { fieldName: 'CDCCEstimatedCompletionDate', displayName: 'CDCC Estimated Completion Date' },
    { fieldName: 'OriginalCompletionDate', displayName: 'Original Completion Date' },
  ] as const;

  for (const c of candidates) {
    const raw = getVal(c.fieldName);
    if (!raw) continue;
    const formatted = formatISODate(raw, false);
    if (formatted) {
      return { name: c.displayName, dateFormatted: formatted };
    }
  }

  return null;
}

/** Schedule main comment - Option 1: OK (not behind) */
export const SCHEDULE_COMMENT_OK =
  'In our opinion the Work is progressing according to schedule and with good weather and well-coordinated construction, the Work should be delivered by the ';

/** Schedule main comment - Option 2: CAN make it up (behind but can recover) */
export const SCHEDULE_COMMENT_CAN_PREFIX = 'In our opinion the Work is not progressing according to schedule and is approximately ';
export const SCHEDULE_COMMENT_CAN_SUFFIX =
  ' weeks behind.  However, since the critical path does not appear to be affected or due to extra time provided later in the schedule, with good weather and well-coordinated construction, the Work could be delivered by the ';

/** Schedule main comment - Option 3: CAN'T make it up */
export const SCHEDULE_COMMENT_CANT_PREFIX = 'In our opinion the Work is not progressing according to schedule and is approximately ';
export const SCHEDULE_COMMENT_CANT_SUFFIX = ' weeks behind.  We do not anticipate the Work meeting the ';

/** Schedule DAMAGES sentence (appended to CAN'T when borrower will assess liquidated damages) */
export const SCHEDULE_COMMENT_DAMAGES_PREFIX =
  'If the Borrower exercises their Liquidated Damages right under the Owner / Contractor Agreement, damages are outlined as "';
export const SCHEDULE_COMMENT_DAMAGES_SUFFIX = ' per day for every day past the Completion Date until Substantial Completion is achieved."';

/** Schedule main comment - Option 4 of 4: AHEAD of schedule */
export const SCHEDULE_COMMENT_AHEAD_SUFFIX = ' As a matter of fact, we believe the project will be completed early.';

/** L&M adequate additional comment */
export const SCHEDULE_COMMENT_LM_ADEQUATE =
  'From our observations during our site visit, the project appears to be supplied with adequate labor and materials for the various trades currently performing work.  Unless otherwise noted in this report, we are not aware of any subcontractor, material, or labor issues that would affect the construction work or progress.';

/** L&M inadequate additional comment */
export const SCHEDULE_COMMENT_LM_INADEQUATE =
  'We are concerned that the project may not be adequately supplied with labor and materials to reach the scheduled Completion Date.';

/**
 * Build the auto-generated schedule section comment text from schedule section fields.
 * Used to populate ScheduledSectionText and the first Comments entry.
 */
export function getScheduleCommentText(data: SectionFieldCollection): string {
  const getVal = (name: string) => data?.fields.find(f => f.fieldName === name)?.value?.trim() ?? '';
  const isTrue = (v: string) => v === 'true' || v === '1';

  const weeksBehindRaw = getVal('WeeksBehindToReport');
  // WeeksBehindToReport is always a non-negative magnitude: 0 = on time, >0 = behind schedule.
  // "Ahead of schedule" is determined separately, from the CDCC Estimated vs. Revised/Original
  // completion dates below - not from the sign of this value.
  const weeksBehindDirection = getWeeksBehindDirection(weeksBehindRaw);
  const isOnTime = weeksBehindDirection === 'onTime';
  const weeksBehindMagnitude = getWeeksBehindMagnitudeDisplay(weeksBehindRaw);

  const cdccEstCompletion = getVal('CDCCEstimatedCompletionDate');
  const revisedCompletion = getVal('RevisedCompletionDate');
  const originalCompletion = getVal('OriginalCompletionDate');
  const scheduledCompletion = revisedCompletion || originalCompletion;

  // Ahead of schedule when the CDCC Estimated Completion Date is before the Revised (or, if
  // blank, Original) Completion Date.
  const cdccEstDate = parseDate(cdccEstCompletion);
  const scheduledCompletionDate = parseDate(scheduledCompletion);
  const isAheadOfSchedule = Boolean(cdccEstDate && scheduledCompletionDate && cdccEstDate.getTime() < scheduledCompletionDate.getTime());

  const canMakeItUp = isTrue(getVal('CanTheyMakeItUp'));
  const willAssessDamages = isTrue(getVal('WillTheBorrowerAssessLiquidatedDamages'));
  const commentOnLaborMaterials = isTrue(getVal('CommentOnLaborMaterials'));
  const laborMaterialsAdequate = isTrue(getVal('LaborMaterialsAdequate'));
  const liquidatedDamagesValue = getVal('LiquidatedDamagesPerContract').trim() || '$0.00';

  const completion = getLatestCompletionDateForSchedule(data);
  const completionPhrase = completion ? `${completion.name} ${completion.dateFormatted}` : 'scheduled completion date';

  const parts: string[] = [];

  if (isAheadOfSchedule) {
    // Ahead - Option 4 of 4
    parts.push(SCHEDULE_COMMENT_OK + completionPhrase + '.' + SCHEDULE_COMMENT_AHEAD_SUFFIX);
  } else if (isOnTime) {
    // Not behind - Option 1 of 4
    parts.push(SCHEDULE_COMMENT_OK + completionPhrase + '.');
  } else if (canMakeItUp) {
    // Behind, but will make it up - Option 2 of 4
    parts.push(SCHEDULE_COMMENT_CAN_PREFIX + weeksBehindMagnitude + SCHEDULE_COMMENT_CAN_SUFFIX + completionPhrase + '.');
  } else {
    // Behind, won't make it up - Option 3 of 4
    parts.push(SCHEDULE_COMMENT_CANT_PREFIX + weeksBehindMagnitude + SCHEDULE_COMMENT_CANT_SUFFIX + completionPhrase + '.');
    // DAMAGES - appended if behind, can't make it up, and borrower will assess liquidated damages
    if (willAssessDamages) {
      parts.push(SCHEDULE_COMMENT_DAMAGES_PREFIX + liquidatedDamagesValue + SCHEDULE_COMMENT_DAMAGES_SUFFIX);
    }
  }

  // L&M additional comments
  if (commentOnLaborMaterials) {
    if (laborMaterialsAdequate) {
      parts.push(SCHEDULE_COMMENT_LM_ADEQUATE);
    } else {
      parts.push(SCHEDULE_COMMENT_LM_INADEQUATE);
    }
  }

  // Join each part on its own line so that any additional comments (damages, labor/materials)
  // appear on new lines instead of being appended after a period.
  return parts.join('\n');
}
